import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha, lighten } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getThemePaths } from '../common/auth/themePaths';
import { FleetIllustration } from './components';
import LogoImage from '../common/auth/LogoImage';
import { useTranslation } from '../common/components/LocalizationProvider';

const backgroundCache = new Map();

const loadImage = (url) => {
  if (!url || typeof window === 'undefined') {
    return Promise.resolve(false);
  }
  if (backgroundCache.has(url)) {
    return Promise.resolve(backgroundCache.get(url));
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      backgroundCache.set(url, true);
      resolve(true);
    };
    img.onerror = () => {
      backgroundCache.set(url, false);
      resolve(false);
    };
    img.src = url;
  });
};

function isAbsolute(url) {
  return /^https?:\/\//i.test(url) || url.startsWith('/');
}

function normalize(url) {
  if (!url) {
    return null;
  }
  if (isAbsolute(url)) {
    return url;
  }
  return `/${url.replace(/^\/+/, '')}`;
}

/* Floating dots - decorative elements for left panel.
   First dot uses brand color; others are fixed accent colors.
   bottom-positioned dots are hidden on mobile to avoid overlapping tagline/logo */
const ACCENT_DOTS = [
  { size: 12, useBrand: true, top: '8%', left: '58%', delay: '0s' },
  { size: 8, light: '#8B5CF6', dark: '#A78BFA', top: '15%', right: '18%', delay: '1s' },
  { size: 10, light: '#EC4899', dark: '#F472B6', bottom: '25%', left: '15%', delay: '2s', mobileHide: true },
  { size: 6, light: '#3B82F6', dark: '#60A5FA', top: '35%', right: '12%', delay: '3s', mobileHide: true },
  { size: 14, light: '#F59E0B', dark: '#FBBF24', bottom: '18%', right: '22%', delay: '0.5s', mobileHide: true },
];

const floatKeyframes = {
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0) scale(1)' },
    '50%': { transform: 'translateY(-12px) scale(1.1)' },
  },
};

const LoginLayout = ({ children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const brand = theme.palette.primary.main;
  const t = useTranslation();
  const revenda = useSelector((state) => state.session.revenda);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [backgroundResolved, setBackgroundResolved] = useState(false);

  const candidates = useMemo(() => {
    const paths = getThemePaths(revenda);
    const themeParam = isDark ? 'dark' : 'light';
    const code = revenda?.code;
    const list = [];

    if (code) {
      list.push(`/api/revendas/background/${code}?theme=${themeParam}`);
    }

    const staticPath = isDark ? paths.backgroundDark : paths.backgroundLight;
    if (staticPath) {
      list.push(staticPath);
    }

    return Array.from(new Set(list));
  }, [revenda, isDark]);

  useEffect(() => {
    let cancelled = false;

    const resolveBackground = async () => {
      for (const candidate of candidates) {
        const normalized = normalize(candidate);
        if (!normalized) {
          continue;
        }
        const ok = await loadImage(normalized);
        if (cancelled) {
          return;
        }
        if (ok) {
          setBackgroundUrl(normalized);
          setBackgroundResolved(true);
          return;
        }
      }
      if (!cancelled) {
        setBackgroundUrl(null);
        setBackgroundResolved(true);
      }
    };

    resolveBackground();
    return () => {
      cancelled = true;
    };
  }, [candidates]);

  const hasBackground = Boolean(backgroundUrl);

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>

      {/* Base fill — always present to avoid blank flash while resolving background */}
      <Box sx={{ position: 'fixed', inset: 0, backgroundColor: isDark ? theme.palette.background.default : '#ffffff', zIndex: 0 }} />

      {/* === BACKGROUND LAYERS === */}
      {backgroundResolved && !hasBackground && (
        <>
          {/* Diagonal cut — desktop: vertical right 60%, mobile: horizontal bottom 63% */}
          <Box
            sx={{
              position: 'fixed',
              right: 0,
              bottom: 0,
              top: { xs: 'auto', md: 0 },
              width: { xs: '100%', md: '60%' },
              height: { xs: '63%', md: 'auto' },
              backgroundColor: isDark ? '#1e1b18' : '#F0EBE3',
              clipPath: {
                xs: 'polygon(0 12%, 100% 0, 100% 100%, 0% 100%)',
                md: 'polygon(18% 0, 100% 0, 100% 100%, 0% 100%)',
              },
              zIndex: 1,
            }}
          />

          {/* Texture overlay on diagonal */}
          <Box
            sx={{
              position: 'fixed',
              right: 0,
              bottom: 0,
              top: { xs: 'auto', md: 0 },
              width: { xs: '100%', md: '60%' },
              height: { xs: '63%', md: 'auto' },
              clipPath: {
                xs: 'polygon(0 12%, 100% 0, 100% 100%, 0% 100%)',
                md: 'polygon(18% 0, 100% 0, 100% 100%, 0% 100%)',
              },
              background: isDark
                ? `radial-gradient(ellipse at 70% 30%, ${alpha(brand, 0.06)} 0%, transparent 60%), radial-gradient(ellipse at 30% 80%, ${alpha(brand, 0.04)} 0%, transparent 50%)`
                : `radial-gradient(ellipse at 70% 30%, ${alpha(brand, 0.04)} 0%, transparent 60%), radial-gradient(ellipse at 30% 80%, ${alpha(brand, 0.03)} 0%, transparent 50%)`,
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        </>
      )}

      {/* === CONTENT CONTAINER === */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          minHeight: '100vh',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >

        {/* === LEFT PANEL — visible on all breakpoints === */}
        <Box
          sx={{
            /* Desktop: fixed 48% width. Mobile: auto height, min 40vh (32vh on small) */
            flex: { xs: 'none', md: '0 0 48%' },
            minHeight: { xs: '32vh', sm: '40vh', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            p: { xs: '1.5rem', sm: 2, md: 3 },
          }}
        >

          {hasBackground && (
            <>
              {/* Revenda custom background */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${backgroundUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              {/* Overlay for legibility */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.4) 100%)',
                  zIndex: 5,
                }}
              />
            </>
          )}

          {/* Logo — absolute top-left */}
          <Box sx={{ position: 'absolute', top: { xs: '1.5rem', md: '2.5rem' }, left: { xs: '1.5rem', md: '2.5rem' }, zIndex: 12 }}>
            <LogoImage
              height={48}
              forceDarkTheme={isDark || hasBackground}
            />
          </Box>

          {/* Floating dots */}
          {backgroundResolved && !hasBackground && ACCENT_DOTS.map((dot, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: dot.size,
                height: dot.size,
                borderRadius: '50%',
                backgroundColor: dot.useBrand
                  ? (isDark ? lighten(brand, 0.2) : brand)
                  : (isDark ? dot.dark : dot.light),
                top: dot.top,
                left: dot.left,
                right: dot.right,
                bottom: dot.bottom,
                animation: `float 6s ease-in-out ${dot.delay} infinite`,
                zIndex: 11,
                ...(dot.mobileHide && { display: { xs: 'none', md: 'block' } }),
                ...floatKeyframes,
              }}
            />
          ))}

          {/* Centered group: illustration + tagline */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Illustration — smaller on mobile (280px vs 440px) */}
            {backgroundResolved && !hasBackground && (
              <Box sx={{ width: '100%', maxWidth: { xs: 280, md: 440 }, px: 2 }}>
                <FleetIllustration />
              </Box>
            )}

            {/* Tagline */}
            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  color: hasBackground ? 'white' : (isDark ? '#e2e8f0' : '#1a1a2e'),
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  mb: 0.35,
                  textShadow: hasBackground ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                }}
              >
                {revenda?.title || t('loginTagline')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: hasBackground ? 'rgba(255,255,255,0.75)' : (isDark ? 'rgba(226,232,240,0.7)' : '#6b7280'),
                  fontSize: '0.88rem',
                  lineHeight: 1.5,
                }}
              >
                {t('loginTaglineDescription')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* === RIGHT PANEL === */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: '1.5rem', sm: 2, md: 4 },
            overflow: 'auto',
            minHeight: { md: '100vh' },
            position: 'relative',
            ...(hasBackground && {
              backgroundColor: isDark ? theme.palette.background.default : '#F0EBE3',
            }),
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 480 }}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginLayout;
